import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  Snackbar,
  Alert,
  useTheme,
  InputAdornment,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Footer from '../components/Footer';

const ContactPage = () => {
  const theme = useTheme();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    restaurant: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Pre-fill form from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const messageParam = searchParams.get('message');
    
    if (messageParam) {
      setFormData(prev => ({
        ...prev,
        message: messageParam
      }));
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only validate if fields have values
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Create WhatsApp message with form data (only include filled fields)
      let whatsappMessage = `Hello! I'm interested in QResto.%0A%0A`;
      
      if (formData.name) whatsappMessage += `Name: ${formData.name}%0A`;
      if (formData.email) whatsappMessage += `Email: ${formData.email}%0A`;
      if (formData.restaurant) whatsappMessage += `Restaurant: ${formData.restaurant}%0A`;
      if (formData.message) whatsappMessage += `Message: ${formData.message}`;
      
      // Open WhatsApp with pre-filled message
      window.open(`https://wa.me/917240255101?text=${whatsappMessage}`, '_blank');
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Redirecting to WhatsApp... Please send the message to complete your inquiry.',
        severity: 'success',
      });
      
      // Reset form after a short delay
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          restaurant: '',
          message: '',
        });
      }, 1000);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: 'background.default',
          py: { xs: 5, sm: 7, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, sm: 4, md: 5 }} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Box sx={{ pr: { md: 5 }, mb: { xs: 4, md: 0 } }}>
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    mb: { xs: 2, sm: 3 },
                    fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                  }}
                >
                  Get in Touch
                </Typography>
                <Typography 
                  variant="h6" 
                  color="text.secondary" 
                  paragraph
                  sx={{ fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}
                >
                  Have questions about our digital menu solution? Want to get started with your restaurant? We'd love to hear from you.
                </Typography>
                
                <Card sx={{ mb: { xs: 3, sm: 4 }, mt: { xs: 3, sm: 4, md: 5 }, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                  <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2.5, sm: 3 } }}>
                      <Box 
                        sx={{ 
                          bgcolor: '#25D366',
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          color: 'white'
                        }}
                      >
                        <WhatsAppIcon />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          WhatsApp / Call
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="medium">
                          <a 
                            href="https://wa.me/917240255101" 
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              textDecoration: 'none',
                              color: theme.palette.text.primary
                            }}
                          >
                            +91 7240255101
                          </a>
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          color: 'white'
                        }}
                      >
                        <LocationOnIcon />
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Our Location
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="medium">
                          Jaipur, Rajasthan
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1552566626-52f8b828add9?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                  alt="Restaurant QR code menu"
                  sx={{
                    width: '100%',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    display: { xs: 'none', lg: 'block' },
                  }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3.5, md: 5 },
                  borderRadius: { xs: 3, md: 4 },
                  bgcolor: 'background.paper',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
                  }}
                >
                  Contact Us
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  paragraph
                  sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}
                >
                  Fill out this form and we'll get back to you as soon as possible.
                </Typography>
                
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: { xs: 3, sm: 4 } }}>
                  <Grid container spacing={{ xs: 2.5, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Restaurant Name"
                        name="restaurant"
                        value={formData.restaurant}
                        onChange={handleChange}
                        error={Boolean(errors.restaurant)}
                        helperText={errors.restaurant}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <RestaurantIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your restaurant and what you're looking for..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={{ py: 1.5, mt: 2 }}
                      >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <Footer />
    </>
  );
};

export default ContactPage;